import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { FormValuesModel } from '.';
import { useFormikContext } from 'formik';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { INTAKE_FORM_FIELDS_GRID_SIZES } from 'constants/grids';
import { TECHNOLOGY_AND_SOFTWARE_DEVELOPMENT_INDUSTRY_CODE } from 'constants/global';
import {
  AmountOptions,
  CustomerOptions,
  SourceOfRevenueOptions,
} from 'enums/onboarding/crm';
import {
  getPlaceholderForSelect,
  getPlaceholderForInputField,
  getPlaceholderForAmountField,
} from '../utils';

// components
import FieldIssue from '../FieldIssue';
import SubmitButtons from '../SubmitButtons';
import HideIfDisabledForm from 'components/Forms/HideIfDisabledForm';
import CustomersSelect, {
  IProps as CustomersSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/CustomersSelect';
import SourceOfRevenueSelect, {
  IProps as SourceOfRevenueSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/SourceOfRevenueSelect';
import ProjectedRevenueSelect, {
  IProps as ProjectedRevenueSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/ProjectedRevenueSelect';
import IndustryCodesAutocomplete, {
  IProps as IndustryCodesAutocompleteProps,
} from 'components/Forms/FormComponents/Autocompletes/CRM/IndustryCodesAutocomplete';
import TrueFalseSelect, {
  IProps as TrueFalseSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/TrueFalseSelect';
import {
  Col,
  Row,
  Divider,
  FormField,
  FormInputTextArea,
  FormInputAmount,
  FormInputTextAreaProps,
  FormInputAmountProps,
} from '@ui';
import DocumentsField from 'components/Forms/FormComponents/DocumentsField';
import VirtualAssetInvolvementSelect, {
  IProps as VirtualAssetInvolvementSelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/VirtualAssetInvolvementSelect';
import SubIndustrySelect, {
  IProps as SubIndustrySelectProps,
} from 'components/Forms/FormComponents/SelectInputs/CRM/SubIndustrySelect';

const InnerForm = () => {
  const { t } = useTranslation('onboarding');
  const { values } = useFormikContext<FormValuesModel>();
  const { getActiveIssueByFieldId } = useEntityIntakeFormContext();

  const disabledParentFields = useMemo(() => {
    const projectedRevenue6mAmountFieldIssue = getActiveIssueByFieldId(
      'projectedRevenue6mAmount',
    );
    const projectedRevenue12mAmountFieldIssue = getActiveIssueByFieldId(
      'projectedRevenue12mAmount',
    );
    const projectedRevenue24mAmountFieldIssue = getActiveIssueByFieldId(
      'projectedRevenue24mAmount',
    );

    const customerGroupOtherDetailsFieldIssue = getActiveIssueByFieldId(
      'customerGroupOtherDetails',
    );

    const sourceOfRevenueOtherDetailsFieldIssue = getActiveIssueByFieldId(
      'sourceOfRevenueOtherDetails',
    );

    const subIndustryFieldIssue = getActiveIssueByFieldId('subIndustries');

    const projectTechnologyFieldIssue = getActiveIssueByFieldId(
      'isBlockchainTechnologyUsed',
    );

    return {
      industryCodes:
        subIndustryFieldIssue?.isResolved === false ||
        projectTechnologyFieldIssue?.isResolved === false,

      projectedRevenueInSixMonths:
        projectedRevenue6mAmountFieldIssue?.isResolved === false,

      projectedRevenueInOneYear:
        projectedRevenue12mAmountFieldIssue?.isResolved === false,

      projectedRevenueInTwoYears:
        projectedRevenue24mAmountFieldIssue?.isResolved === false,

      customers: customerGroupOtherDetailsFieldIssue?.isResolved === false,

      sourceOfRevenue:
        sourceOfRevenueOtherDetailsFieldIssue?.isResolved === false,
    };
  }, [getActiveIssueByFieldId]);

  const shouldRenderCustomersInfoField = useMemo(() => {
    return values.customers.includes(CustomerOptions.Other);
  }, [values.customers]);

  const shouldRenderSourceOfRevenueInfoField = useMemo(() => {
    return values.sourceOfRevenue.includes(SourceOfRevenueOptions.Other);
  }, [values.sourceOfRevenue]);

  const shouldRenderProjectTechnologyField = useMemo(() => {
    return values.industryCodes.includes(
      TECHNOLOGY_AND_SOFTWARE_DEVELOPMENT_INDUSTRY_CODE,
    );
  }, [values.industryCodes]);

  return (
    <>
      <Row wrap={true} justify="start">
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<IndustryCodesAutocompleteProps>
            label={
              <FieldIssue
                fieldId="industryCodes"
                isDisabledDueToChildComments={
                  disabledParentFields.industryCodes
                }
              >
                {t('intake_form.customers_and_products.industry_codes')}
              </FieldIssue>
            }
            name="industryCodes"
            component={IndustryCodesAutocomplete}
            additionalProps={{
              mode: 'multiple',
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, true, t),
            }}
          />
        </Col>
        {values.industryCodes.length ? (
          <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
            <FormField<SubIndustrySelectProps>
              label={
                <FieldIssue fieldId="subIndustries">
                  {t('intake_form.customers_and_products.sub_industry')}
                </FieldIssue>
              }
              name="subIndustries"
              component={SubIndustrySelect}
              additionalProps={{
                mode: 'multiple',
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForSelect(validationRules, true, true, t),
              }}
            />
          </Col>
        ) : null}
        {shouldRenderProjectTechnologyField ? (
          <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
            <FormField<TrueFalseSelectProps>
              label={
                <FieldIssue fieldId="isBlockchainTechnologyUsed">
                  {t('intake_form.customers_and_products.project_technology')}
                </FieldIssue>
              }
              name="isBlockchainTechnologyUsed"
              component={TrueFalseSelect}
              additionalProps={{
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForSelect(validationRules, true, false, t),
              }}
            />
          </Col>
        ) : null}
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<TrueFalseSelectProps>
            label={
              <FieldIssue fieldId="isVirtualAssetsInvolvedInBusiness">
                {t(
                  'intake_form.customers_and_products.use_blockchain_technology',
                )}
              </FieldIssue>
            }
            name="isVirtualAssetsInvolvedInBusiness"
            component={TrueFalseSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, false, false, t),
            }}
          />
        </Col>
        {values.isVirtualAssetsInvolvedInBusiness ? (
          <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
            <FormField<VirtualAssetInvolvementSelectProps>
              label={
                <FieldIssue fieldId="involvedVirtualAssets">
                  {t(
                    'intake_form.customers_and_products.virtual_asset_options',
                  )}
                </FieldIssue>
              }
              name="involvedVirtualAssets"
              component={VirtualAssetInvolvementSelect}
              additionalProps={{
                mode: 'multiple',
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForSelect(validationRules, false, true, t),
              }}
            />
          </Col>
        ) : null}
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<CustomersSelectProps>
            label={
              <FieldIssue
                fieldId="customerGroups"
                isDisabledDueToChildComments={disabledParentFields.customers}
              >
                {t('intake_form.customers_and_products.customers')}
              </FieldIssue>
            }
            disabled={disabledParentFields.customers}
            name="customers"
            component={CustomersSelect}
            additionalProps={{
              mode: 'multiple',
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, true, t),
            }}
          />
        </Col>
        {shouldRenderCustomersInfoField ? (
          <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
            <FormField<FormInputTextAreaProps>
              label={
                <FieldIssue fieldId="customerGroupOtherDetails">
                  {t('intake_form.customers_and_products.customers_other')}
                </FieldIssue>
              }
              name="customerOtherDetails"
              component={FormInputTextArea}
              additionalProps={{
                rows: 5,
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForInputField(validationRules, true, t),
              }}
            />
          </Col>
        ) : null}
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<SourceOfRevenueSelectProps>
            label={
              <FieldIssue
                fieldId="sourcesOfRevenue"
                isDisabledDueToChildComments={
                  disabledParentFields.sourceOfRevenue
                }
              >
                {t('intake_form.customers_and_products.source_of_revenue')}
              </FieldIssue>
            }
            disabled={disabledParentFields.sourceOfRevenue}
            name="sourceOfRevenue"
            component={SourceOfRevenueSelect}
            additionalProps={{
              mode: 'multiple',
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, true, t),
            }}
          />
        </Col>
        {shouldRenderSourceOfRevenueInfoField ? (
          <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
            <FormField<FormInputTextAreaProps>
              label={
                <FieldIssue fieldId="sourceOfRevenueOtherDetails">
                  {t(
                    'intake_form.customers_and_products.source_of_revenue_other',
                  )}
                </FieldIssue>
              }
              name="sourceOfRevenueOtherDetails"
              component={FormInputTextArea}
              additionalProps={{
                rows: 5,
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForInputField(validationRules, true, t),
              }}
            />
          </Col>
        ) : null}
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputTextAreaProps>
            label={
              <FieldIssue fieldId="businessDescription">
                {t('intake_form.customers_and_products.business_info')}
              </FieldIssue>
            }
            name="businessInfo"
            component={FormInputTextArea}
            additionalProps={{
              rows: 5,
              renderPlaceholder: (validationRules) =>
                getPlaceholderForInputField(validationRules, true, t),
            }}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<FormInputTextAreaProps>
            label={
              <FieldIssue fieldId="supportingDocuments">
                {t('intake_form.customers_and_products.supporting_documents')}
              </FieldIssue>
            }
            name="supportingDocuments"
            component={DocumentsField}
          />
        </Col>
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<ProjectedRevenueSelectProps>
            label={
              <FieldIssue
                fieldId="projectedRevenue6m"
                isDisabledDueToChildComments={
                  disabledParentFields.projectedRevenueInSixMonths
                }
              >
                {t('intake_form.customers_and_products.revenue_in_six_months')}
              </FieldIssue>
            }
            disabled={disabledParentFields.projectedRevenueInSixMonths}
            name="projectedRevenueInSixMonths"
            component={ProjectedRevenueSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        {values.projectedRevenueInSixMonths === AmountOptions.Above1m ? (
          <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
            <FormField<FormInputAmountProps>
              label={
                <FieldIssue fieldId="projectedRevenue6mAmount">
                  {t('intake_form.customers_and_products.amount_of_revenue')}
                </FieldIssue>
              }
              name="projectedRevenueInSixMonthsAmount"
              component={StyledFormInputAmount}
              additionalProps={{
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForAmountField(validationRules, true, t),
              }}
            />
          </Col>
        ) : null}
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<ProjectedRevenueSelectProps>
            label={
              <FieldIssue
                fieldId="projectedRevenue12m"
                isDisabledDueToChildComments={
                  disabledParentFields.projectedRevenueInOneYear
                }
              >
                {t('intake_form.customers_and_products.revenue_in_one_year')}
              </FieldIssue>
            }
            disabled={disabledParentFields.projectedRevenueInOneYear}
            name="projectedRevenueInOneYear"
            component={ProjectedRevenueSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        {values.projectedRevenueInOneYear === AmountOptions.Above1m ? (
          <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
            <FormField<FormInputAmountProps>
              label={
                <FieldIssue fieldId="projectedRevenue12mAmount">
                  {t('intake_form.customers_and_products.amount_of_revenue')}
                </FieldIssue>
              }
              name="projectedRevenueInOneYearAmount"
              component={StyledFormInputAmount}
              additionalProps={{
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForAmountField(validationRules, true, t),
              }}
            />
          </Col>
        ) : null}
        <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
          <FormField<ProjectedRevenueSelectProps>
            label={
              <FieldIssue
                fieldId="projectedRevenue24m"
                isDisabledDueToChildComments={
                  disabledParentFields.projectedRevenueInTwoYears
                }
              >
                {t('intake_form.customers_and_products.revenue_in_two_years')}
              </FieldIssue>
            }
            disabled={disabledParentFields.projectedRevenueInTwoYears}
            name="projectedRevenueInTwoYears"
            component={ProjectedRevenueSelect}
            additionalProps={{
              renderPlaceholder: (validationRules) =>
                getPlaceholderForSelect(validationRules, true, false, t),
            }}
          />
        </Col>
        {values.projectedRevenueInTwoYears === AmountOptions.Above1m ? (
          <Col {...INTAKE_FORM_FIELDS_GRID_SIZES}>
            <FormField<FormInputAmountProps>
              label={
                <FieldIssue fieldId="projectedRevenue24mAmount">
                  {t('intake_form.customers_and_products.amount_of_revenue')}
                </FieldIssue>
              }
              name="projectedRevenueInTwoYearsAmount"
              component={StyledFormInputAmount}
              additionalProps={{
                renderPlaceholder: (validationRules) =>
                  getPlaceholderForAmountField(validationRules, true, t),
              }}
            />
          </Col>
        ) : null}
      </Row>
      <HideIfDisabledForm>
        <Divider />
        <SubmitButtons />
      </HideIfDisabledForm>
    </>
  );
};

const StyledFormInputAmount = styled(FormInputAmount)`
  width: 100%;
`;

export default InnerForm;
